import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Globe, Upload, Check, FileText } from 'lucide-react';
import guestTranslations from '@/i18n/guestTranslations';
import { getNationalities, getNationalityName } from '@/data/nationalityTranslations';

const LANGS = [
  { code: 'it', label: 'IT' },
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
  { code: 'fr', label: 'FR' },
];

const APARTMENTS = {
  suite: 'Suite Apartment',
  bellavista: 'Bellavista',
  oasis: 'Oasis',
  crisalide: 'Crisalide',
  emerald: 'Emerald Apartment',
};

const VALID_APARTMENTS = Object.keys(APARTMENTS);

const detectLang = () => {
  const bl = (navigator.language || '').slice(0, 2).toLowerCase();
  return ['it', 'en', 'de', 'fr'].includes(bl) ? bl : 'en';
};

const GuestRegistration = () => {
  const { apartment } = useParams();
  const navigate = useNavigate();
  const [lang, setLang] = useState(detectLang);
  const t = guestTranslations[lang];
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [natSearch, setNatSearch] = useState('');
  const [natOpen, setNatOpen] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [docUploaded, setDocUploaded] = useState(false);
  const [docFileId, setDocFileId] = useState('');
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    surname: '', name: '', email: '', birth_date: '', arrival_date: '', departure_date: '',
    nationality: '', document_type: '', adult_companions: '', child_companions: '',
  });

  const allNats = useMemo(() => getNationalities(lang), [lang]);

  const filteredNats = useMemo(() => {
    if (!natSearch) return allNats;
    const s = natSearch.toLowerCase();
    return allNats.filter(n =>
      n.name.toLowerCase().includes(s) || n.code.toLowerCase().includes(s)
    );
  }, [natSearch, allNats]);

  useEffect(() => {
    if (!VALID_APARTMENTS.includes(apartment)) {
      window.location.href = '/';
    }
  }, [apartment]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (!VALID_APARTMENTS.includes(apartment)) return null;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      setError(t.documentUploadHint);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Max 10MB');
      return;
    }
    setDocFile(file);
    setDocUploaded(false);
    setDocFileId('');
    setError('');
  };

  const uploadDocument = async (registrationId) => {
    if (!docFile) return false;
    setDocUploading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const formData = new FormData();
      formData.append('file', docFile);
      formData.append('registration_id', registrationId);
      const res = await fetch(`${API}/api/guest/upload-document?registration_id=${registrationId}`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setDocFileId(data.file_id);
      setDocUploaded(true);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setDocUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.surname || !form.name || !form.email || !form.birth_date || !form.arrival_date || !form.departure_date || !form.nationality || !form.document_type) {
      setError(t.required);
      return;
    }
    if (!docFile && !docUploaded) {
      setError(t.documentRequired);
      return;
    }
    if (form.adult_companions === '' || form.child_companions === '') {
      setError(t.companionsRequired);
      return;
    }
    if (form.departure_date <= form.arrival_date) {
      setError(t.invalidDates);
      return;
    }

    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${API}/api/guest/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apartment,
          surname: form.surname,
          name: form.name,
          email: form.email,
          birth_date: form.birth_date,
          arrival_date: form.arrival_date,
          departure_date: form.departure_date,
          nationality: form.nationality,
          document_type: form.document_type,
          adult_companions: parseInt(form.adult_companions),
          child_companions: parseInt(form.child_companions),
          lang,
        }),
      });
      if (!res.ok) throw new Error('Registration failed');
      const data = await res.json();

      // Upload document - block navigation if it fails
      const uploadSuccess = await uploadDocument(data.id);
      if (!uploadSuccess) {
        setLoading(false);
        return;
      }

      navigate(`/${apartment}/tax?registration_id=${data.id}&lang=${lang}`);
      sessionStorage.setItem('guest_lang', lang);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const aptName = APARTMENTS[apartment] || apartment;

  return (
    <div className="min-h-screen bg-white" data-testid="guest-registration-page">
      <meta name="robots" content="noindex, nofollow" />
      {/* Header */}
      <div className="bg-[#65c3ba] text-white py-6 px-4">
        <div className="max-w-xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{t.pageTitle}</h1>
            <p className="text-white/80 text-sm mt-1">{aptName} — {t.subtitle}</p>
          </div>
          <div className="flex gap-1">
            {LANGS.map(l => (
              <button key={l.code} onClick={() => setLang(l.code)}
                className={`px-2 py-1 text-xs rounded ${lang === l.code ? 'bg-white text-[#3e716c] font-bold' : 'bg-white/20 text-white'}`}
                data-testid={`guest-lang-${l.code}`}>{l.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="guest-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[#0A1312] text-sm font-medium">{t.surname} *</Label>
              <Input value={form.surname} onChange={e => handleChange('surname', e.target.value)} data-testid="guest-surname" className="mt-1" />
            </div>
            <div>
              <Label className="text-[#0A1312] text-sm font-medium">{t.name} *</Label>
              <Input value={form.name} onChange={e => handleChange('name', e.target.value)} data-testid="guest-name" className="mt-1" />
            </div>
          </div>

          <div>
            <Label className="text-[#0A1312] text-sm font-medium">{t.email} *</Label>
            <Input type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} data-testid="guest-email" className="mt-1" />
          </div>

          <div>
            <Label className="text-[#0A1312] text-sm font-medium">{t.birthDate} *</Label>
            <Input type="date" value={form.birth_date} onChange={e => handleChange('birth_date', e.target.value)} data-testid="guest-birthdate" className="mt-1" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[#0A1312] text-sm font-medium">{t.arrivalDate} *</Label>
              <Input type="date" value={form.arrival_date} onChange={e => handleChange('arrival_date', e.target.value)} data-testid="guest-arrival" className="mt-1" />
            </div>
            <div>
              <Label className="text-[#0A1312] text-sm font-medium">{t.departureDate} *</Label>
              <Input type="date" value={form.departure_date} onChange={e => handleChange('departure_date', e.target.value)} data-testid="guest-departure" className="mt-1" />
            </div>
          </div>

          {/* Nationality with search */}
          <div className="relative">
            <Label className="text-[#0A1312] text-sm font-medium">{t.nationality} *</Label>
            <div className="mt-1">
              <Input
                placeholder={t.searchNationality}
                value={natSearch}
                onChange={e => { setNatSearch(e.target.value); setNatOpen(true); }}
                onFocus={() => setNatOpen(true)}
                data-testid="guest-nationality-search"
              />
              {form.nationality && !natOpen && (
                <p className="text-xs text-[#65c3ba] mt-1">
                  {getNationalityName(form.nationality, lang)} ({form.nationality})
                </p>
              )}
              {natOpen && (
                <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-[60vh] overflow-y-auto mt-1">
                  {filteredNats.map(n => (
                    <button key={n.code} type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#F7F9F9] flex justify-between"
                      onClick={() => { handleChange('nationality', n.code); setNatSearch(n.name); setNatOpen(false); }}
                      data-testid={`nat-${n.code}`}>
                      <span>{n.name}</span>
                      <span className="text-gray-400 text-xs">{n.code}</span>
                    </button>
                  ))}
                  {filteredNats.length === 0 && <p className="px-3 py-2 text-sm text-gray-400">Nessun risultato</p>}
                </div>
              )}
            </div>
          </div>

          <div>
            <Label className="text-[#0A1312] text-sm font-medium">{t.documentType} *</Label>
            <Select value={form.document_type} onValueChange={v => handleChange('document_type', v)}>
              <SelectTrigger className="mt-1" data-testid="guest-doctype">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="passport">{t.passport}</SelectItem>
                <SelectItem value="identity_card">{t.identityCard}</SelectItem>
                <SelectItem value="other">{t.other}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Document Upload */}
          <div>
            <Label className="text-[#0A1312] text-sm font-medium">{t.documentUpload} *</Label>
            <div className="mt-1">
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={handleFileChange}
                className="hidden"
                data-testid="guest-doc-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`w-full border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                  docFile || docUploaded
                    ? 'border-[#65c3ba] bg-[#65c3ba]/5'
                    : 'border-gray-300 hover:border-[#65c3ba] hover:bg-[#F7F9F9]'
                }`}
                data-testid="guest-doc-upload-btn"
              >
                {docUploading ? (
                  <div className="flex items-center justify-center gap-2 text-[#65c3ba]">
                    <div className="w-4 h-4 border-2 border-[#65c3ba] border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">{t.documentUploading}</span>
                  </div>
                ) : docFile ? (
                  <div className="flex items-center justify-center gap-2 text-[#3e716c]">
                    <FileText size={18} />
                    <span className="text-sm font-medium">{docFile.name}</span>
                    <span className="text-xs text-[#0A1312]/40">({(docFile.size / 1024).toFixed(0)} KB)</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <Upload size={20} className="text-gray-400" />
                    <span className="text-sm text-gray-500">{t.documentUploadHint}</span>
                  </div>
                )}
              </button>
              <p className="text-[11px] text-[#0A1312]/50 mt-1.5 leading-relaxed">{t.documentPrivacy}</p>
              <p className="text-[11px] text-[#0A1312]/50 mt-1 leading-relaxed">{t.documentRedactHint}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[#0A1312] text-sm font-medium">{t.adultCompanions} *</Label>
              <Select value={form.adult_companions} onValueChange={v => handleChange('adult_companions', v)}>
                <SelectTrigger className="mt-1" data-testid="guest-adults">
                  <SelectValue placeholder={t.selectCompanions} />
                </SelectTrigger>
                <SelectContent>
                  {[0,1,2,3,4].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#0A1312] text-sm font-medium">{t.childCompanions} *</Label>
              <Select value={form.child_companions} onValueChange={v => handleChange('child_companions', v)}>
                <SelectTrigger className="mt-1" data-testid="guest-children">
                  <SelectValue placeholder={t.selectCompanions} />
                </SelectTrigger>
                <SelectContent>
                  {[0,1,2,3,4].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {error && <p className="text-red-500 text-sm" data-testid="guest-error">{error}</p>}

          <Button type="submit" disabled={loading || docUploading} data-testid="guest-submit"
            className="w-full bg-[#65c3ba] text-white hover:bg-[#3e716c] py-6 text-lg rounded-sm">
            {loading ? t.submitting : t.submit}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default GuestRegistration;
